export type AuthProvider = "guest" | "discord" | "twitch" | "username";

export interface AuthIdentity {
  id: string;
  name: string;
  image?: string | null;
  provider: AuthProvider;
  role: string;
  isStreamer: boolean;
  isAnonymous: boolean;
  linkedProviders: Array<"discord" | "twitch">;
}

export type AuthUser = {
  id: string;
  name: string;
  image?: string | null;
  isAnonymous?: boolean | null;
  role?: string | null;
};

export type LinkedAccount = {
  providerId: string;
};

export type GameplayTokenClaims = {
  name?: unknown;
  image?: unknown;
  provider?: unknown;
  isStreamer?: unknown;
};

const AUTH_PROVIDERS: readonly AuthProvider[] = [
  "guest",
  "discord",
  "twitch",
  "username",
];

/** Every provider value the provider badge knows how to render. */
export const BADGE_PROVIDERS: readonly AuthProvider[] = AUTH_PROVIDERS;

export function isBadgeProvider(provider: unknown): provider is AuthProvider {
  return (
    typeof provider === "string" &&
    (AUTH_PROVIDERS as readonly string[]).includes(provider)
  );
}

/**
 * The provider badge always comes from the gameplay token's provider claim:
 * the provider of the player's most recent sign-in (last-login-wins). Claims
 * are optional so identity loading can degrade gracefully when the token is
 * unavailable.
 */
export function providerFromClaims(
  claims: GameplayTokenClaims | null | undefined,
): AuthProvider | null {
  const provider = claims?.provider;
  return typeof provider === "string" &&
    (AUTH_PROVIDERS as readonly string[]).includes(provider)
    ? (provider as AuthProvider)
    : null;
}

export function isStreamerFromClaims(
  claims: GameplayTokenClaims | null | undefined,
): boolean {
  return claims?.isStreamer === true;
}

/**
 * Builds the browser identity from the session user and linked accounts.
 * The name is the single stored display name; the provider falls back to
 * linked-provider precedence only when the token claims are unavailable.
 */
export function identityFrom(
  user: AuthUser,
  accounts: readonly LinkedAccount[],
  claims?: GameplayTokenClaims | null,
): AuthIdentity {
  const linkedProviders: Array<"discord" | "twitch"> = [];
  if (accounts.some((account) => account.providerId === "twitch")) {
    linkedProviders.push("twitch");
  }
  if (accounts.some((account) => account.providerId === "discord")) {
    linkedProviders.push("discord");
  }
  const hasCredential = accounts.some(
    (account) => account.providerId === "credential",
  );
  const provider =
    providerFromClaims(claims) ??
    (linkedProviders.includes("twitch")
      ? "twitch"
      : linkedProviders.includes("discord")
        ? "discord"
        : hasCredential || user.isAnonymous !== true
          ? "username"
          : "guest");

  return {
    id: user.id,
    name: user.name,
    image: user.image ?? null,
    provider,
    role: user.role || "user",
    isStreamer: isStreamerFromClaims(claims),
    isAnonymous: user.isAnonymous === true,
    linkedProviders,
  };
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest;

  const account = (providerId: string) => ({ providerId });

  describe("providerFromClaims", () => {
    it("takes the provider badge from the token claim", () => {
      // Last sign-in was with username even though Twitch is linked.
      expect(providerFromClaims({ provider: "username", name: "Alice" })).toBe(
        "username",
      );
      expect(providerFromClaims({ provider: "twitch" })).toBe("twitch");
      expect(providerFromClaims({ provider: "guest" })).toBe("guest");
      expect(providerFromClaims({ provider: "discord" })).toBe("discord");
    });

    it("rejects unknown or malformed providers", () => {
      expect(providerFromClaims({ provider: "unknown" })).toBeNull();
      expect(providerFromClaims({ provider: 42 })).toBeNull();
      expect(providerFromClaims(null)).toBeNull();
      expect(providerFromClaims(undefined)).toBeNull();
    });
  });

  describe("identityFrom", () => {
    it("uses the stored display name and the claim-based badge", () => {
      const identity = identityFrom(
        {
          id: "u1",
          name: "TwitchGuy",
          image: "https://example.com/avatar.png",
        },
        [account("twitch"), account("credential")],
        { provider: "username", name: "TwitchGuy", isStreamer: true },
      );
      expect(identity.name).toBe("TwitchGuy");
      expect(identity.image).toBe("https://example.com/avatar.png");
      expect(identity.provider).toBe("username");
      expect(identity.isStreamer).toBe(true);
      expect(identity.linkedProviders).toEqual(["twitch"]);
    });

    it("falls back to linked-provider precedence without claims", () => {
      const identity = identityFrom({ id: "u1", name: "TwitchGuy" }, [
        account("twitch"),
        account("credential"),
      ]);
      expect(identity.provider).toBe("twitch");
      expect(identity.isStreamer).toBe(false);
    });

    it("resolves guests from the session user", () => {
      const identity = identityFrom(
        { id: "u1", name: "AlpineExplorer123", isAnonymous: true },
        [],
        { provider: "guest" },
      );
      expect(identity.provider).toBe("guest");
      expect(identity.isAnonymous).toBe(true);
    });
  });
}
